'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes"],
  _excluded2 = ["contextualNodeAttributes"];
import { BulletCircleIcon, BulletListIcon, BulletLowerAlphaIcon, BulletLowerGreekIcon, BulletLowerRomanIcon, BulletSquareIcon, BulletUpperAlphaIcon, BulletUpperRomanIcon, NumberListIcon } from 'foundations-assets';
import ListMenu from 'rich-text-lib/components/formatting/ListMenu';
import CanvasToolbarButton from 'rich-text-lib/components/toolbar/ToolbarButton';
import { curry } from 'rich-text-lib/utils/iterables';
import tinymce from 'tinymce';
import { i18nNameSpace } from '../constants/hsformatting';
import { CANVAS_BULLETED_LIST, CANVAS_BULLETED_LIST_BUTTON, CANVAS_NUMBERED_LIST, CANVAS_NUMBERED_LIST_BUTTON } from '../constants/toolbar';
import { i18nWithNameSpace } from '../utils/i18n';
import { isFirstChild } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace(i18nNameSpace);
const listNodes = ['LI', 'UL', 'OL', 'DL'];
const getListChange = (editor, nodeName, defaultValue, isInsert = false) => value => {
  editor.undoManager.transact(() => {
    const {
      dom,
      selection
    } = editor;
    const command = nodeName === 'UL' ? 'InsertUnorderedList' : 'InsertOrderedList';
    const newValue = value === defaultValue ? null : value;
    let list = dom.getParent(selection.getNode(), 'ul,ol');
    if (isInsert || !list || list.nodeName !== nodeName) {
      editor.execCommand(command);
    }
    list = dom.getParent(selection.getNode(), 'ul,ol');
    if (list) {
      dom.setStyle(list, 'listStyleType', newValue);
      // Ported from the adv list plugin https://git.hubteam.com/HubSpot/tinymce/blob/master/static/js/tinymce/plugins/advlist/plugin.js#L57
      list.removeAttribute('data-mce-style');
    }
    editor.focus();
  });
};
const listButtonControls = [{
  controlName: CANVAS_BULLETED_LIST_BUTTON,
  tooltip: 'tooltip.bulletlist',
  nodeName: 'UL',
  styleType: {
    iconName: 'bulletList',
    icon: BulletListIcon,
    value: 'default'
  }
}, {
  controlName: CANVAS_NUMBERED_LIST_BUTTON,
  tooltip: 'tooltip.numlist',
  nodeName: 'OL',
  styleType: {
    iconName: 'numberList',
    icon: NumberListIcon,
    value: 'default'
  }
}];
const listControls = [{
  controlName: CANVAS_BULLETED_LIST,
  tooltip: 'tooltip.bulletlist',
  nodeName: 'UL',
  options: [{
    icon: 'bulletList',
    Icon: BulletListIcon,
    value: 'default'
  }, {
    icon: 'bulletCircle',
    Icon: BulletCircleIcon,
    value: 'circle'
  }, {
    icon: 'bulletSquare',
    Icon: BulletSquareIcon,
    value: 'square'
  }]
}, {
  controlName: CANVAS_NUMBERED_LIST,
  tooltip: 'tooltip.numlist',
  nodeName: 'OL',
  options: [{
    icon: 'numberList',
    Icon: NumberListIcon,
    value: 'default'
  }, {
    icon: 'bulletUpperRoman',
    Icon: BulletUpperRomanIcon,
    value: 'upper-roman'
  }, {
    icon: 'bulletUpperAlpha',
    Icon: BulletUpperAlphaIcon,
    value: 'upper-alpha'
  }, {
    icon: 'bulletLowerRoman',
    Icon: BulletLowerRomanIcon,
    value: 'lower-roman'
  }, {
    icon: 'bulletLowerAlpha',
    Icon: BulletLowerAlphaIcon,
    value: 'lower-alpha'
  }, {
    icon: 'bulletLowerGreek',
    Icon: BulletLowerGreekIcon,
    value: 'lower-greek'
  }]
}];
const indentControlHandler = editor => {
  const blocks = editor.selection.getSelectedBlocks();
  let isDisabled = false;
  for (let i = 0, l = blocks.length; !isDisabled && i < l; i++) {
    const tag = blocks[i].nodeName;
    isDisabled = tag === listNodes[0] && isFirstChild(blocks[i]) || listNodes.indexOf(tag) > 0;
  }
  editor.fire(`FormatChanged:indent`, {
    isDisabled
  });
};
export function addListButtons(editor) {
  const nodeChangeHandlerList = [];

  // List controls as select menu
  listControls.forEach(({
    controlName,
    tooltip,
    nodeName,
    options
  }) => {
    const defaultValue = options[0].value;
    const handleClick = getListChange(editor, nodeName, defaultValue, true);
    tinymce.canvas.addButton(editor, controlName, {
      renderControl: props => {
        const {
            contextualNodeAttributes
          } = props,
          rest = _objectWithoutPropertiesLoose(props, _excluded);
        const {
          [controlName]: {
            isActive,
            listStyleType = defaultValue
          } = {}
        } = contextualNodeAttributes;
        return /*#__PURE__*/_jsx(ListMenu, Object.assign({
          dropdownClassName: "mce-hubspot-menu",
          active: isActive,
          value: listStyleType,
          onClick: () => handleClick(listStyleType),
          splitButtonTestId: controlName,
          tooltip: i18nKey(tooltip),
          onListTypeChange: getListChange(editor, nodeName, defaultValue),
          options: options
        }, rest));
      }
    });
    nodeChangeHandlerList.push(({
      parents,
      selectionChange
    }) => {
      if (!parents) {
        return {};
      }
      if (selectionChange) {
        indentControlHandler(editor);
      }
      const parentNode = parents.find(parent => parent.nodeName === nodeName);
      const formattingAttributes = {
        isActive: false
      };
      if (parentNode) {
        formattingAttributes.isActive = true;
        formattingAttributes.listStyleType = editor.dom.getStyle(parentNode, 'listStyleType') || defaultValue;
      }
      return {
        [controlName]: formattingAttributes
      };
    });
  });
  const isActiveHandler = curry((nodeName, event) => {
    if (!event.parents) return false;
    return !!event.parents.find(parent => parent.nodeName === nodeName);
  });

  // List controls as button
  listButtonControls.forEach(({
    controlName,
    tooltip,
    nodeName,
    styleType
  }) => {
    const {
      iconName,
      icon,
      value
    } = styleType;
    const handleListChange = getListChange(editor, nodeName, value, true);
    tinymce.canvas.addButton(editor, controlName, {
      renderControl: props => {
        const {
            contextualNodeAttributes
          } = props,
          rest = _objectWithoutPropertiesLoose(props, _excluded2);
        const {
          [controlName]: isActive
        } = contextualNodeAttributes;
        return /*#__PURE__*/_jsx(CanvasToolbarButton, Object.assign({
          "data-test-id": controlName,
          iconName: iconName,
          icon: icon,
          tooltip: i18nKey(tooltip),
          onClick: () => handleListChange(value),
          selected: isActive
        }, rest));
      }
    });
    nodeChangeHandlerList.push(evt => ({
      [controlName]: isActiveHandler(nodeName, evt)
    }));
  });
  const getFormattingAttributes = evt => nodeChangeHandlerList.reduce((acc, nodeChangeHandler) => Object.assign({}, acc, nodeChangeHandler(evt)), {});
  tinymce.canvas.addNodeChangeHandler(editor, {
    getFormattingAttributes
  });
}